import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Container } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { FiLogOut } from "react-icons/fi";
import { Link as ReactRouterLink } from "react-router-dom";
import { RegistererState } from "sip.js";
import {
  Popover,
  IconButton,
  Box,
  Typography,
  Badge,
  ListItemText,
  ListItem,
  List,
  Tabs,
  Tab,
  Link,
  Tooltip,
  Button,
} from "@mui/material";
import DialpadIcon from "@mui/icons-material/Dialpad";
import NotificationsIcon from "@mui/icons-material/Notifications";
import PhoneIcon from "@mui/icons-material/Phone";
import SearchIcon from "@mui/icons-material/Search";
import apiClient from "../../services/apiClient";

import { useSIP } from "../../hooks/calls/useSIP";
import {
  clearUser,
  updateUserDetails,
} from "../../features/user/UserSlice";
import { setModal, setSubModal } from "../../features/modal/ModalSlice";
import { removeSipConfig } from "../../features/SIP/SIPConfigSlice";
import CustomInputBox from "../Form/CustomInputBox";
import CustomButton from "../Button/CustomButton";
import { IoNotifications, IoNotificationsOutline } from "react-icons/io5";

const Header = () => {
  const [notificationAnchorEl, setNotificationAnchorEl] = useState(null);
  const [phoneAnchorEl, setPhoneAnchorEl] = useState(null);
  const [selectedTab, setSelectedTab] = useState(0);
  const [pending, setPending] = useState([]);
  const [approved, setApproved] = useState([]);

  const notificationOpen = Boolean(notificationAnchorEl);
  const phoneOpen = Boolean(phoneAnchorEl);

  const notificationPopoverId = notificationOpen
    ? "notification-popover"
    : undefined;
  const phonePopoverId = phoneOpen ? "phone-popover" : undefined;
  const tabContent = selectedTab === 0 ? pending : approved;

  const { regState } = useSelector((state) => state.SIPCalls);
  const SIPConfig = useSelector((state) => state.SIPConfig);
  const userInfo = useSelector((state) => state.userInfo.user);

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { connectSIP, disconnectSIP } = useSIP();

  useEffect(() => {
    fetchDetails();
    if (userInfo?.role === "2") fetchRequests();
  }, []);

  const fetchRequests = async () => {
    try {
      const response = await apiClient.get(
        `/requests/user?user_id=${userInfo?.id}`
      );

      const data = response?.data || [];
      const pendingRequests = data.filter(
        (item) =>
          item?.request_status === "pending" ||
          item?.request_status === "Pending"
      );
      const approvedRequests = data.filter(
        (item) => item?.request_status === "approved"
      );

      setPending(pendingRequests);
      setApproved(approvedRequests);
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  const fetchDetails = async () => {
    try {
      const response = await apiClient.get(`/user/${userInfo?.id}`);

      const extension = response?.data?.user?.settings?.default_extension;
      dispatch(updateUserDetails(response?.data?.user));

      if (!extension?.extension) return;
      const SipConfig = {
        sipUsername: extension?.extension,
        sipPassword: extension?.password,
        sipServer: userInfo?.company_domain,
        webSocketUrl: process.env.REACT_APP_VOIP_WEBSOCKET_BASE_URL,
      };

      connectSIP(SipConfig);
    } catch (err) {
      console.log(err);
    }
  };

  const handleConnect = () => {
    const SipConfig = {
      sipUsername: SIPConfig?.sipUsername,
      sipPassword: SIPConfig?.sipPassword,
      sipServer: userInfo?.company_domain,
      webSocketUrl: process.env.REACT_APP_VOIP_WEBSOCKET_BASE_URL,
    };

    connectSIP(SipConfig);
  };

  const handleDisconnect = () => {
    disconnectSIP();
  };

  const handleLogout = () => {
    dispatch(clearUser());
    dispatch(removeSipConfig());
    navigate("/");
  };

  const handleLoginPopUp = () => {
    dispatch(setModal({ type: "LOGIN" }));
  };

  const handleSignupPopUp = () => {
    dispatch(setModal({ type: "SIGNUP" }));
  };

  const handleDialCall = () => {
    handlePhonePopoverClose();
    dispatch(setModal({ type: "CALL_DIALPAD" }));
  };

  const handleNotificationClick = (event) => {
    setNotificationAnchorEl(event.currentTarget);
  };

  const handlePhoneClick = (event) => {
    setPhoneAnchorEl(event.currentTarget);
  };

  const handleNotificationPopoverClose = () => {
    setNotificationAnchorEl(null);
  };

  const handlePhonePopoverClose = () => {
    setPhoneAnchorEl(null);
  };

  const handleChange = (event, newValue) => {
    setSelectedTab(newValue);
  };

  const handleOutBoundCall = () => {
    dispatch(setSubModal({ type: "COMMONCALL" }));
  };

  return (
    <Box
      component="header"
      sx={{
        position: "sticky",
        top: 0,
        zIndex: 99,
        backgroundColor: "#fff",
        borderBottom: "1px solid #E5E7EB",
        p: 2,
      }}
    >
      
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          {/* Search Bar */}
          <Box sx={{ flex: "0 1 500px" }}>
            <CustomInputBox
              icon={SearchIcon}
              placeholder="Search calls, recordings..."
              type="text"
              width="100%"
            />
          </Box>

          {/* Right Side Actions */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            {userInfo ? (
              <>
                {/* Notifications - Admin Only */}
                {userInfo?.role === "2" && (
                  <>
                    <Tooltip title="Notifications">
                      <IconButton
                        onClick={handleNotificationClick}
                        size="small"
                        sx={{
                          color: "#6B7280",
                          backgroundColor: "#edf2fcff",
                          padding: 1.2,
                          "&:hover": { backgroundColor: "#F3F4F6" },
                        }}
                      >
                        <Badge
                          badgeContent={pending.length}
                          color="error"
                          overlap="circular"
                        >
                          {/* <NotificationsIcon size={30} /> */}
                          <IoNotificationsOutline size={26} />
                        </Badge>
                      </IconButton>
                    </Tooltip>

                    <Popover
                      id={notificationPopoverId}
                      open={notificationOpen}
                      anchorEl={notificationAnchorEl}
                      onClose={handleNotificationPopoverClose}
                      anchorOrigin={{
                        vertical: "bottom",
                        horizontal: "right",
                      }}
                      transformOrigin={{
                        vertical: "top",
                        horizontal: "right",
                      }}
                      sx={{
                        "& .MuiPopover-paper": {
                          borderRadius: "12px",
                          boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
                          mt: 1,
                        },
                      }}
                    >
                      <Box sx={{ minWidth: 320, maxHeight: 400 }}>
                        <Tabs
                          value={selectedTab}
                          onChange={handleChange}
                          variant="fullWidth"
                          sx={{
                            borderBottom: 1,
                            borderColor: "divider",
                            "& .MuiTab-root": {
                              textTransform: "none",
                              fontWeight: 500,
                              fontSize: "0.875rem",
                            },
                          }}
                        >
                          <Tab label={`Pending (${pending.length})`} />
                          <Tab label={`Approved (${approved.length})`} />
                        </Tabs>

                        <Box sx={{ maxHeight: 300, overflowY: "auto", p: 1 }}>
                          {tabContent.length > 0 ? (
                            <List disablePadding>
                              {tabContent.map((item) => (
                                <ListItem
                                  key={item.id}
                                  sx={{
                                    borderRadius: "8px",
                                    "&:hover": { backgroundColor: "#F9FAFB" },
                                  }}
                                >
                                  <ListItemText
                                    primary={
                                      selectedTab === 1
                                        ? `Your request of ${
                                            item.no_of_phones_required
                                          } phone number on ${new Date(
                                            item.created_at
                                          ).toLocaleDateString()} has been approved.`
                                        : `Your request of ${
                                            item.no_of_phones_required
                                          } phone number on ${new Date(
                                            item.created_at
                                          ).toLocaleDateString()} is pending.`
                                    }
                                    primaryTypographyProps={{
                                      fontSize: "0.813rem",
                                      color: "#374151",
                                    }}
                                  />
                                </ListItem>
                              ))}
                            </List>
                          ) : (
                            <Typography
                              sx={{
                                textAlign: "center",
                                py: 4,
                                color: "#9CA3AF",
                                fontSize: "0.875rem",
                              }}
                            >
                              No notifications
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </Popover>
                  </>
                )}

                {/* Inbound Call - User Role Only */}
                {userInfo?.role === "3" && (
                  <>
                    <Tooltip title="Inbound Call">
                      <Button
                        onClick={handlePhoneClick}
                        variant="contained"
                        size="small"
                        startIcon={<PhoneIcon fontSize="small" />}
                        sx={{
                          backgroundColor: "#6366F1",
                          textTransform: "none",
                          fontWeight: 500,
                          fontSize: "0.875rem",
                          borderRadius: "8px",
                          px: 2,
                          py: 0.75,
                          boxShadow: "none",
                          "&:hover": {
                            backgroundColor: "#4F46E5",
                            boxShadow: "0 2px 8px rgba(99, 102, 241, 0.3)",
                          },
                        }}
                      >
                        <Badge
                          color={
                            regState === RegistererState.Registered
                              ? "success"
                              : "error"
                          }
                          variant="dot"
                          sx={{ "& .MuiBadge-badge": { right: -6, top: -2 } }}
                        >
                          Inbound
                        </Badge>
                      </Button>
                    </Tooltip>

                    <Popover
                      id={phonePopoverId}
                      open={phoneOpen}
                      anchorEl={phoneAnchorEl}
                      onClose={handlePhonePopoverClose}
                      anchorOrigin={{
                        vertical: "bottom",
                        horizontal: "right",
                      }}
                      transformOrigin={{
                        vertical: "top",
                        horizontal: "right",
                      }}
                      sx={{
                        "& .MuiPopover-paper": {
                          borderRadius: "12px",
                          boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
                          mt: 1,
                        },
                      }}
                    >
                      {SIPConfig?.sipUsername ? (
                        <Box sx={{ p: 3, minWidth: 280, textAlign: "center" }}>
                          <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                            {regState === RegistererState.Registered
                              ? "Connected"
                              : "Connect To Extension"}
                          </Typography>

                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mb: 3 }}
                          >
                            {regState === RegistererState.Registered
                              ? `Connected to ${SIPConfig?.sipUsername}`
                              : `Register ${SIPConfig?.sipUsername} to start calls`}
                          </Typography>

                          {regState === RegistererState.Registered ? (
                            <Box sx={{ display: "flex", gap: 1 }}>
                              <CustomButton
                                variant="contained"
                                icon={<DialpadIcon />}
                                iconPosition="left"
                                fullWidth
                                onClick={handleDialCall}
                              >
                                Call
                              </CustomButton>
                              <CustomButton
                                variant="outlined"
                                fullWidth
                                onClick={handleDisconnect}
                              >
                                Disconnect
                              </CustomButton>
                            </Box>
                          ) : (
                            <CustomButton
                              variant="contained"
                              fullWidth
                              onClick={handleConnect}
                            >
                              Connect
                            </CustomButton>
                          )}
                        </Box>
                      ) : (
                        <Box sx={{ p: 3, minWidth: 280, textAlign: "center" }}>
                          <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                            No Extension
                          </Typography>

                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mb: 2 }}
                          >
                            You don't have any{" "}
                            <Link
                              component={ReactRouterLink}
                              to="/extensions"
                              underline="hover"
                            >
                              extension
                            </Link>{" "}
                            assigned.
                          </Typography>

                          <Typography variant="body2" color="text.secondary">
                            Contact your admin
                          </Typography>
                        </Box>
                      )}
                    </Popover>
                  </>
                )}

                {/* Outbound Call Button */}
                <Tooltip title="Outbound Call">
                  <Button
                    onClick={handleOutBoundCall}
                    variant="contained"
                    size="small"
                    startIcon={<PhoneIcon fontSize="small" />}
                    sx={{
                      backgroundColor: "#3B82F6",
                      textTransform: "none",
                      fontWeight: 500,
                      fontSize: "0.875rem",
                      borderRadius: "8px",
                      px: 2,
                      py: 0.75,
                      boxShadow: "none",
                      "&:hover": {
                        backgroundColor: "#2563EB",
                        boxShadow: "0 2px 8px rgba(59, 130, 246, 0.3)",
                      },
                    }}
                  >
                    Outbound
                  </Button>
                </Tooltip>

                {/* Logout Button */}
                <Tooltip title="Logout">
                  <IconButton
                    onClick={handleLogout}
                    size="small"
                    sx={{
                      color: "#FFFFFF",
                      padding: 1.2,
                      backgroundColor: "#fd6161ff",
                      "&:hover": { backgroundColor: "#FEE2E2", color: "#DC2626" },
                    }}
                  >
                    <FiLogOut size={22} />
                  </IconButton>
                </Tooltip>
              </>
            ) : (
              <Box sx={{ display: "flex", gap: 1 }}>
                <CustomButton
                  text="Login"
                  onClick={handleLoginPopUp}
                  variant="text"
                />
                <CustomButton
                  text="Sign Up"
                  onClick={handleSignupPopUp}
                  variant="outlined"
                />
              </Box>
            )}
          </Box>
        </Box>
      
    </Box>
  );
};

export default Header;
